/**
 * The auth side panel: a miniature of the dashboard waiting behind the form.
 * Nobody is signed in yet, so it plays a sample workspace — notifications
 * land in the feed and the counters tick up — to show the product at work.
 */
import { Avatar, Badge, Logo, Skeleton, Stack, StatusDot, surface, Text, useMediaQuery } from "@nudgebox/ui";
import { ChevronsUpDownIcon, DocsIcon, HomeIcon, KeyIcon } from "@nudgebox/ui/icons";
import "@nudgebox/ui/tokens.stylex";
import { color, font, fontSize, fontWeight, lineHeight, motion, radius, space } from "@nudgebox/ui/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { type ReactNode, useEffect, useState } from "react";
import { formatNumber, formatPercent } from "@/lib/format.ts";
const SAMPLE_APPS = ["Acme Store", "Status Page", "Field Notes"] as const;

/** What the sample workspace sends, in order; the feed loops through it. */
const SAMPLE_NOTIFICATIONS: {
  app: (typeof SAMPLE_APPS)[number];
  title: string;
  devices: number;
}[] = [{
  app: "Acme Store",
  title: "Your order has shipped",
  devices: 1
}, {
  app: "Status Page",
  title: "API latency back to normal",
  devices: 412
}, {
  app: "Field Notes",
  title: "New post: Shipping faster in 2026",
  devices: 2_318
}, {
  app: "Acme Store",
  title: "Flash sale starts in 10 minutes",
  devices: 5_904
}, {
  app: "Status Page",
  title: "Scheduled maintenance tonight",
  devices: 412
}, {
  app: "Acme Store",
  title: "Back in stock: Trail runners",
  devices: 87
}];
const FEED_SIZE = 4;
const TICK_MS = 2_600;
export function AuthPreview() {
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  // How many notifications have "arrived". Starts with a full feed so the panel never looks empty.
  const [sent, setSent] = useState(FEED_SIZE);
  useEffect(() => {
    if (reducedMotion) return;
    const id = window.setInterval(() => setSent(count => count + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, [reducedMotion]);
  const feed = Array.from({
    length: FEED_SIZE
  }, (_, index) => {
    const position = sent - 1 - index;
    return {
      key: position,
      ...SAMPLE_NOTIFICATIONS[position % SAMPLE_NOTIFICATIONS.length]!
    };
  });
  const delivered = 182_406 + sent * 1_317;
  const latest = feed[0]!;
  return <div aria-hidden inert className="x10l6tqk x38wyzf x1sf83jk x3m8u43 x1ey2m1c xb3r6kr x2sz766 x14p2s33 x47corl x87ps6o x16hbdh0">
      <div className="x78zum5 xpcbtbh x1ovqmqt x1np647f">
        <div {...stylex.props(surface.raised, surface.md, styles.sidebar)}>
          <div className="x1uu5im9 x18dy136">
            <Logo />
          </div>

          <div className="x78zum5 x6s0dn4 x1a62zme xc9qbxq x1uu5im9 xxe3v3u">
            <Avatar name="Acme" size={22} square />
            <span className="x1iyjqo2 xeuugli xluwven x1ivwu1z x9aqtqa">Acme</span>
            <Badge tone="accent">Owner</Badge>
            <ChevronsUpDownIcon size={14} />
          </div>

          <NavRow selected icon={<HomeIcon size={15} />}>
            Overview
          </NavRow>

          <Stack gap="none">
            <SectionLabel>Applications</SectionLabel>
            {SAMPLE_APPS.map(app => <NavRow key={app} icon={<Avatar name={app} size={16} square />} live={latest.app === app}>
                {app}
              </NavRow>)}
          </Stack>

          <Stack gap="none">
            <SectionLabel>Developer</SectionLabel>
            <NavRow icon={<KeyIcon size={15} />}>API keys</NavRow>
            <NavRow icon={<DocsIcon size={15} />}>Documentation</NavRow>
          </Stack>

          <div className="xr1yuqi xt1dizb x1kpc7t0 x178xt8z x13fuv20 x8yrp0t">
            <div className="x78zum5 x6s0dn4 x1a62zme x1gnnpzl x1uu5im9 xmkeg23 x1y0btm7 x9r1u3d xoazux2 xluwven x1219144 x187ovcu">
              <Skeleton width={22} height={22} radius="full" />
              <Skeleton width="60%" height={12} />
            </div>
          </div>
        </div>

        <div {...stylex.props(surface.raised, surface.md, styles.main)}>
          <div className="x78zum5 xdt5ytf x9p7km9 xxc7z9f">
            <Stack gap="xs">
              <div className="x6cdl7o x1ivwu1z xjat59b x9aqtqa">Acme</div>
              <Text size="sm" tone="tertiary">
                Your notification infrastructure at a glance.
              </Text>
            </Stack>

            <div {...stylex.props(surface.raised, surface.sm, surface.plain, styles.strip)}>
              <MiniMetric label="Subscribers" value={formatNumber(8_721 + Math.floor(sent / 2))} />
              <MiniMetric label="Delivered" value={formatNumber(delivered)} />
              <MiniMetric label="Success" value={formatPercent(0.998)} />
            </div>

            <Stack gap="md">
              <div className="x78zum5 x6s0dn4 x1qughib">
                <Text size="sm" weight="medium">
                  Recent notifications
                </Text>
                <span className="x3nfvp2 x6s0dn4 x1a62zme xxg9qwl xmccgnw xxe3v3u">
                  <span {...{
                  0: {
                    className: "x1v4s8kt xols6we x257jf6 xiltt8s x13q19t4 x18hd8ej xa4qsjk"
                  },
                  1: {
                    className: "x1v4s8kt xols6we x257jf6 xiltt8s x18hd8ej xa4qsjk x1a5igra"
                  }
                }[!!reducedMotion << 0]} />
                  Live
                </span>
              </div>
              <div className="x78zum5 xdt5ytf xlfxrz8 xmkeg23 x1y0btm7 x8nrc2f xb3r6kr">
                {feed.map((item, index) =>
              // Keyed by position in the stream, so only the newest row mounts (and animates in).
              <div key={item.key} {...{
                0: {
                  className: "x78zum5 x6s0dn4 xpcbtbh xk7tk9l xws8vp0 x178xt8z x1g31smg x13fuv20 x8yrp0t"
                },
                1: {
                  className: "x78zum5 x6s0dn4 xpcbtbh xk7tk9l xws8vp0 x178xt8z x1g31smg x13fuv20 x8yrp0t xye01m3 x1i47e9z xxn97zd"
                }
              }[!!(index === 0) << 0]}>
                    <Avatar name={item.app} size={28} square />
                    <Stack gap="none" grow>
                      <Text size="sm" weight="medium" truncate>
                        {item.title}
                      </Text>
                      <Text size="xs" tone="tertiary" truncate>
                        {item.app} · {item.devices === 1 ? "1 device" : `${formatNumber(item.devices)} devices`}
                      </Text>
                    </Stack>
                    {index === 0 && !reducedMotion ? <StatusDot tone="warning">Sending</StatusDot> : <StatusDot tone="success">Delivered</StatusDot>}
                  </div>)}
              </div>
            </Stack>
          </div>
        </div>
      </div>
    </div>;
}
function SectionLabel({
  children
}: {
  children: ReactNode;
}) {
  return <div className="x1uu5im9 x2sz766 x18dy136 xxg9qwl xmccgnw xxe3v3u">{children}</div>;
}
function NavRow({
  icon,
  selected = false,
  live = false,
  children
}: {
  icon: ReactNode;
  selected?: boolean;
  live?: boolean;
  children: ReactNode;
}) {
  return <div {...stylex.props(styles.navRow, selected && surface.raised, selected && surface.xs, selected && styles.selected)}>
      <span className="x78zum5 xxe3v3u">{icon}</span>
      <span className="x1iyjqo2 xeuugli xb3r6kr xlyipyv xuxw1ft">{children}</span>
      <span {...{
      0: {
        className: "x1v4s8kt xols6we x257jf6 xiltt8s xg01cxk x19991ni xjx9puj"
      },
      1: {
        className: "x1v4s8kt xols6we x257jf6 xiltt8s x19991ni xjx9puj x1hc1fzr"
      }
    }[!!live << 0]} />
    </div>;
}
function MiniMetric({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="x78zum5 xdt5ytf xjenv9e xeuugli">
      <span className="xxg9qwl xmccgnw xxe3v3u xuxw1ft">{label}</span>
      <span className="xxg9qwl x6cdl7o xo2cfqc xfpseah x9aqtqa xss6m8b">{value}</span>
    </div>;
}
const enter = "xaq1gtq-B";
const pulse = "x1i5vc86-B";
const styles = {
  sidebar: {
    kzqmXN: "xy2penl",
    kmuXW: "x2lah0s",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xpcbtbh",
    kLKAdn: "x549ohs",
    kg3NbH: "xsef1i0",
    kaIpWk: "x16l8lxw",
    $$css: true
  },
  navRow: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1a62zme",
    kZKoxP: "x1gnnpzl",
    kg3NbH: "x1uu5im9",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x9r1u3d",
    kaIpWk: "xoazux2",
    kGuDYH: "xluwven",
    k63SB2: "x1219144",
    kMwMTN: "x187ovcu",
    $$css: true
  },
  selected: {
    kMwMTN: "x9aqtqa",
    $$css: true
  },
  main: {
    kzQI83: "x1iyjqo2",
    k7Eaqz: "xeuugli",
    kmVPX3: "x1jgqtc6",
    kaIpWk: "x16l8lxw",
    $$css: true
  },
  strip: {
    k1xSpc: "xrvj5dj",
    kumcoG: "x185a7wo",
    kOIVth: "x4l6xbm",
    k8WAf4: "x3eux3o",
    kg3NbH: "xws8vp0",
    kaIpWk: "xlfxrz8",
    $$css: true
  }
};